<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useSidebar } from '@/composables/useSidebar'
import { useThemeSwitch } from '@/composables/useThemeSwitch'
import { useAuth } from '@/composables/useAuth'
import { useUserProfile } from '@/features/user/composables/useUserProfile'
import { useDatapoints } from '@/composables/useDatapoints'
import SidebarNavItem from './SidebarNavItem.vue'
import type { NavItem } from '@/types/navigation.types'

const { isExpanded, isMobile, isMobileOpen, toggle, close } = useSidebar()
const { isDark, toggleTheme } = useThemeSwitch()
const { logout } = useAuth()
const { userEmail, isAdmin } = useUserProfile()
const {
	enablePreferredTitles,
	enableExternalSofoApi,
	disableCrmExport,
	enableAiAssistant,
	enableBackOfficeIndex,
	enableLeadEnrichment,
	enableLookAlike,
	hidePeopleSearch,
	fetchDatapoints,
} = useDatapoints()

// Lead Scoring and ICP are always shown in the nav — their pages render a "not enabled"
// state when the datapoint is disabled. People Search, Lead Enrichment and LookAlike ARE
// gated here (via each item's featureFlag) so they disappear from the nav entirely.
const featureFlagMap: Record<string, () => boolean> = {
	ENABLE_PREFERRED_TITLES: () => enablePreferredTitles.value,
	ENABLE_EXTERNAL_SOFO_API: () => enableExternalSofoApi.value,
	DISABLE_CRM_EXPORT: () => !disableCrmExport.value,
	ENABLE_AI_ASSISTANT: () => enableAiAssistant.value,
	BACK_OFFICE_INDEX: () => enableBackOfficeIndex.value,
	ENABLE_LEAD_ENRICHMENT: () => enableLeadEnrichment.value,
	ENABLE_LOOK_A_LIKE_SERVICE: () => enableLookAlike.value,
	// Inverted: the item is hidden while HIDE_PEOPLE_SEARCH is set.
	HIDE_PEOPLE_SEARCH: () => !hidePeopleSearch.value,
}

onMounted(() => {
	fetchDatapoints()
})

const navItems: NavItem[] = [
	{
		title: 'Dashboard',
		icon: 'fa-solid fa-gauge',
		to: '/',
	},
	{
		title: 'Getting Started',
		icon: 'fa-solid fa-rocket',
		to: '/onboarding',
	},
	{
		title: 'List Builder',
		icon: 'fa-solid fa-list',
		to: '/listbuilder',
	},
	{
		title: 'People Search',
		icon: 'fa-solid fa-user',
		to: '/people-search',
		featureFlag: 'HIDE_PEOPLE_SEARCH',
	},
	{
		title: 'My Lists',
		icon: 'fa-solid fa-rectangle-list',
		to: '/my-lists',
	},
	{
		title: 'Lead Enrichment',
		icon: 'fa-solid fa-wand-magic-sparkles',
		to: '/lead-enrichment',
		featureFlag: 'ENABLE_LEAD_ENRICHMENT',
	},
	{
		title: 'LookAlike',
		icon: 'fa-solid fa-clone',
		to: '/lookalike',
		featureFlag: 'ENABLE_LOOK_A_LIKE_SERVICE',
	},
	{
		title: 'Lead Scoring',
		icon: 'fa-regular fa-star',
		to: '/lead-scoring',
	},
	{
		title: 'ICP',
		icon: 'fa-solid fa-bullseye',
		to: '/icp',
	},
	{
		title: 'AI Assistant',
		icon: 'fa-solid fa-robot',
		to: '/ai-assistant',
		featureFlag: 'ENABLE_AI_ASSISTANT',
	},
	{
		title: 'MCP Connector',
		icon: 'fa-solid fa-plug',
		to: '/mcp-connector',
	},
	{
		// Top level, beside MCP Connector; its page keeps the /settings/api-access address.
		title: 'API Access',
		icon: 'fa-solid fa-key',
		to: '/settings/api-access',
		featureFlag: 'ENABLE_EXTERNAL_SOFO_API',
	},
	{
		title: 'Subscription',
		icon: 'fa-solid fa-receipt',
		to: '/subscription',
	},
	{
		title: 'Purchase History',
		icon: 'fa-solid fa-clock-rotate-left',
		children: [
			{
				title: 'Leads Purchased',
				icon: 'fa-solid fa-cart-shopping',
				to: '/purchase-history/leads-purchased',
			},
			{
				title: 'Bulk Downloads',
				icon: 'fa-solid fa-download',
				to: '/purchase-history/bulk-downloads',
				// The detail page is '/purchase-history/bulk-download/:id' (singular), which the
				// plural `to` never matches — keep this item highlighted while viewing one.
				activeMatch: ['/purchase-history/bulk-download'],
			},
			{
				title: 'Ignored Leads',
				icon: 'fa-solid fa-user-slash',
				to: '/purchase-history/ignored-leads',
			},
		],
	},
	{
		title: 'Settings',
		icon: 'fa-solid fa-gear',
		children: [
			{
				title: 'CRM Setup',
				icon: 'fa-solid fa-plug',
				to: '/settings/crm-setup',
				featureFlag: 'DISABLE_CRM_EXPORT',
			},
			{
				title: 'Preferred Titles',
				icon: 'fa-solid fa-heading',
				to: '/settings/preferred-titles',
				featureFlag: 'ENABLE_PREFERRED_TITLES',
			},
			{
				title: 'Report Formats',
				icon: 'fa-solid fa-chart-column',
				to: '/settings/report-formats',
			},
			{
				title: 'User Details',
				icon: 'fa-solid fa-users',
				to: '/settings/user-details',
				adminOnly: true,
			},
			{
				title: 'User Credits',
				icon: 'fa-solid fa-credit-card',
				to: '/settings/user-credits',
				adminOnly: true,
			},
			{
				title: 'GDPR',
				icon: 'fa-solid fa-shield-halved',
				to: '/settings/gdpr',
				adminOnly: true,
			},
		],
	},
	{
		title: 'Chrome Plugin',
		icon: 'fa-brands fa-chrome',
		href: 'https://chromewebstore.google.com/detail/tami/bimgikcfhcjibdaaamjlogpndceajhii',
		external: true,
	},
	{
		title: 'Sales Support Hub',
		icon: 'fa-solid fa-headset',
		to: '/sales-support-hub',
		featureFlag: 'BACK_OFFICE_INDEX',
	},
]

const visibleItems = computed(() =>
	navItems.filter((item) => {
		if (item.adminOnly && !isAdmin.value) return false
		if (item.featureFlag) {
			const check = featureFlagMap[item.featureFlag]
			if (check && !check()) return false
		}
		return true
	}),
)

// Title of the rail group whose flyout is showing. Held here rather than per item so opening
// one group's flyout closes its neighbour's at once, instead of the two panels overlapping
// while the first one waits out its close delay.
const openFlyout = ref<string | null>(null)

function onFlyoutToggle(title: string, open: boolean) {
	if (open) openFlyout.value = title
	else if (openFlyout.value === title) openFlyout.value = null
}

// Flyouts only exist in the desktop rail; drop the state on leaving it so a stale value
// can't pop a flyout open the next time the sidebar collapses. Re-apply the active group's open
// state too: accordion groups only exist in the expanded sidebar, so a rail → expanded switch
// mounts them fresh and they must re-open for the current route.
watch([isExpanded, isMobile], () => {
	openFlyout.value = null
	nextTick(syncOpenGroupToRoute)
})

const route = useRoute()

// Does this item own the current route (by `to` or `activeMatch`)? Mirrors the highlight
// logic in SidebarNavItem.isActive for the SPA-link cases, so the open group matches the
// highlighted child exactly.
function itemMatchesPath(item: NavItem, path: string): boolean {
	if (item.to && (path === item.to || path.startsWith(item.to + '/'))) return true
	return item.activeMatch?.some((m) => path === m || path.startsWith(m + '/')) ?? false
}

// Open accordion groups (by title) for the expanded sidebar. Controlled so the group holding
// the active route can be expanded on a fresh load — an uncontrolled v-list starts every group
// collapsed, hiding the highlighted child (e.g. opening a Bulk Download detail directly).
const openedGroups = ref<string[]>([])

// Ensure the group that owns the current route is expanded, without collapsing groups the user
// opened by hand (merge, don't replace).
function syncOpenGroupToRoute(): void {
	const activeGroup = visibleItems.value.find((item) =>
		item.children?.some((child) => itemMatchesPath(child, route.path)),
	)
	if (activeGroup && !openedGroups.value.includes(activeGroup.title)) {
		openedGroups.value = [...openedGroups.value, activeGroup.title]
	}
}

// Route CHANGES after mount sync immediately. The INITIAL open, however, must wait until the
// list's groups have registered their values with Vuetify — setting `opened` during setup is
// normalized away because the target group does not exist yet. nextTick (after the child
// v-list-groups have mounted) is the first moment the open takes effect.
watch(() => route.path, syncOpenGroupToRoute)
onMounted(() => nextTick(syncOpenGroupToRoute))

const drawerModelValue = computed({
	get: () => (isMobile.value ? isMobileOpen.value : true),
	set: (val: boolean) => {
		if (isMobile.value && !val) close()
	},
})

const userInitial = computed(() => {
	const email = userEmail.value || ''
	return email.charAt(0).toUpperCase() || 'U'
})

async function onLogout() {
	await logout()
}
</script>

<template>
	<v-navigation-drawer
		v-model="drawerModelValue"
		:rail="!isMobile && !isExpanded"
		:temporary="isMobile"
		:permanent="!isMobile"
		:expand-on-hover="false"
		theme="tamiDark"
		class="app-sidebar"
		width="260"
		rail-width="64"
	>
		<!-- Logo — links back to the dashboard (Vue 2 parity: the header logo is a home link) -->
		<router-link
			to="/"
			class="app-sidebar__logo d-flex align-center"
			:class="{ 'justify-center': !isExpanded && !isMobile }"
			aria-label="Go to dashboard"
		>
			<img
				v-if="isExpanded || isMobile"
				src="@/assets/Tami_white_low.png"
				alt="TAMI"
				height="28"
				class="app-sidebar__logo-full"
			/>
			<img
				v-else
				src="@/assets/icon_fit.svg"
				alt="TAMI"
				height="28"
				class="app-sidebar__logo-icon"
			/>
		</router-link>

		<v-divider
			class="mx-3 my-1"
			style="opacity: 0.15"
		/>

		<!-- Toggle button -->
		<v-tooltip
			:text="isExpanded ? 'Collapse menu' : 'Expand menu'"
			location="right"
		>
			<template #activator="{ props: tip }">
				<button
					v-if="!isMobile"
					v-bind="tip"
					class="app-sidebar__toggle"
					:aria-label="isExpanded ? 'Collapse menu' : 'Expand menu'"
					@click="toggle"
				>
					<i
						class="fa-solid fa-chevron-left app-sidebar__toggle-icon"
						:class="{ 'app-sidebar__toggle-icon--flipped': !isExpanded }"
					/>
				</button>
			</template>
		</v-tooltip>

		<!-- Navigation -->
		<v-list
			v-model:opened="openedGroups"
			density="compact"
			nav
			class="app-sidebar__nav"
		>
			<SidebarNavItem
				v-for="item in visibleItems"
				:key="item.title"
				:item="item"
				:is-admin="isAdmin"
				:rail="!isExpanded && !isMobile"
				:flyout-open="openFlyout === item.title"
				@update:flyout-open="onFlyoutToggle(item.title, $event)"
				@navigate="isMobile ? close() : undefined"
			/>
		</v-list>

		<v-spacer />

		<!-- Footer -->
		<template #append>
			<v-divider
				class="mx-3"
				style="opacity: 0.15"
			/>
			<div class="app-sidebar__footer pa-2">
				<!-- Theme toggle -->
				<v-tooltip
					:text="isDark ? 'Light mode' : 'Dark mode'"
					location="end"
				>
					<template #activator="{ props: tip }">
						<v-btn
							v-bind="tip"
							variant="text"
							size="small"
							class="app-sidebar__theme-btn"
							@click="toggleTheme"
						>
							<i :class="isDark ? 'fa-solid fa-sun' : 'fa-solid fa-moon'" />
							<span
								v-if="isExpanded || isMobile"
								class="ml-2 text-caption"
							>
								{{ isDark ? 'Light mode' : 'Dark mode' }}
							</span>
						</v-btn>
					</template>
				</v-tooltip>

				<!-- User menu: anchored to the bottom-most sidebar button; opens UPWARD and is teleported
				     to the body so it is never clipped by the sidebar's overflow. -->
				<v-menu
					location="top end"
					:close-on-content-click="true"
				>
					<template #activator="{ props: menuProps }">
						<v-btn
							v-bind="menuProps"
							variant="text"
							size="small"
							class="app-sidebar__user-btn mt-1"
							:class="{ 'justify-center': !isExpanded && !isMobile }"
						>
							<v-avatar
								size="28"
								color="primary"
								class="mr-2"
							>
								<span class="text-caption font-weight-bold text-white">{{ userInitial }}</span>
							</v-avatar>
							<span
								v-if="isExpanded || isMobile"
								class="app-sidebar__user-email text-caption"
							>
								{{ userEmail || 'User' }}
							</span>
						</v-btn>
					</template>
					<v-list density="compact">
						<v-list-item :to="{ name: 'Profile' }">
							<template #prepend>
								<i class="fa-solid fa-user mr-3" />
							</template>
							<v-list-item-title>Profile</v-list-item-title>
						</v-list-item>
						<v-divider />
						<v-list-item @click="onLogout">
							<template #prepend>
								<i class="fa-solid fa-right-from-bracket mr-3" />
							</template>
							<v-list-item-title>Log Off</v-list-item-title>
						</v-list-item>
					</v-list>
				</v-menu>
			</div>
		</template>
	</v-navigation-drawer>
</template>

<style scoped lang="scss">
.app-sidebar {
	border-right: none !important;
	background: linear-gradient(
		135deg,
		$shell-gradient-start 0%,
		$shell-gradient-end 100%
	) !important;
	transition: width $sidebar-transition !important;

	&::before {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background:
			radial-gradient(ellipse at 20% 0%, rgba(70, 200, 222, 0.08) 0%, transparent 60%),
			radial-gradient(ellipse at 80% 100%, rgba(137, 42, 84, 0.1) 0%, transparent 50%);
		pointer-events: none;
		z-index: 0;
	}

	&__logo {
		padding: 16px 16px 8px;
		min-height: 56px;
		cursor: pointer;

		&:hover {
			opacity: 0.85;
		}
	}

	&__logo-full {
		transition: opacity 200ms ease;
	}

	&__logo-icon {
		height: 28px;
		object-fit: contain;
	}

	&__toggle {
		position: absolute;
		top: 50px;
		right: -12px;
		z-index: 10;
		width: 24px;
		height: 24px;
		border-radius: 50%;
		border: 1px solid rgba(255, 255, 255, 0.15);
		background: $shell-gradient-end;
		color: rgba(255, 255, 255, 0.7);
		cursor: pointer;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 10px;
		transition:
			opacity 200ms ease,
			background-color 200ms ease,
			border-color 200ms ease;
		// Discoverable at rest, prominent on interest
		opacity: 0.4;

		&:hover {
			background: $tami-cyan;
			color: #fff;
			border-color: $tami-cyan;
			opacity: 1;
		}
	}

	&:hover &__toggle {
		opacity: 1;
	}

	&__toggle-icon {
		transition: transform $sidebar-transition;

		&--flipped {
			transform: rotate(180deg);
		}
	}

	&__nav {
		padding-top: 4px;
		background: transparent !important;
	}

	&__footer {
		display: flex;
		flex-direction: column;
	}

	&__theme-btn {
		justify-content: flex-start;
		width: 100%;
		color: rgba(255, 255, 255, 0.7) !important;
		text-transform: none;
		letter-spacing: 0;

		&:hover {
			color: #ffffff !important;
			background-color: rgba(255, 255, 255, 0.08);
		}
	}

	&__user-btn {
		justify-content: flex-start;
		width: 100%;
		text-transform: none;
		letter-spacing: 0;
		color: rgba(255, 255, 255, 0.9) !important;

		&:hover {
			background-color: rgba(255, 255, 255, 0.08);
		}
	}

	&__user-email {
		max-width: 160px;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
}

// Global overrides for v-list-group chevrons inside the sidebar
.app-sidebar :deep(.v-list-group__header) {
	.v-list-item__append {
		.v-icon {
			font-size: 14px !important;
			opacity: 0.6;
		}
	}
}

// Ensure proper overflow behavior in rail mode
.app-sidebar :deep(.v-navigation-drawer__content) {
	overflow-x: hidden;
}

// Labels fade with the width change so the collapse reads as one motion
.app-sidebar :deep(.v-list-item-title) {
	transition: opacity 150ms ease;
}

// Hide subtitles and appended chevrons when collapsed (rail mode); titles stay in the DOM
// and fade out instead of popping away. Groups render as flyouts in rail (SidebarNavFlyout).
.app-sidebar.v-navigation-drawer--rail {
	:deep(.v-list-item-title) {
		opacity: 0;
	}

	:deep(.v-list-item-subtitle),
	:deep(.v-list-item__append) {
		display: none !important;
	}

	:deep(.v-list-item--active) {
		padding-right: 28px;
	}
}
</style>
